import * as Sentry from '@sentry/react'
import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import { ABILITIES } from '@/components/Can'
import { useCaptureEvent } from '@/hooks'
import { selectUserRole } from '@/reducers/userDataReducer'
import { generateId } from '@/utils'

const SCREEN = 'property_settings'

const SETTING_NAME = 'iglooWorkCustomPinAvailable'

const INTEGRATION_PROVIDER = 'iglooworks'

const MAX_ERROR_MESSAGE_LENGTH = 100

export const CUSTOM_PIN_SETTING_NOT_SAVED_MESSAGE = 'Igloo custom PIN setting was not saved'

export type CustomPinSettingFailureReason =
  | 'code_conflict'
  | 'forbidden'
  | 'integration_missing'
  | 'network_error'
  | 'parent_disabled'
  | 'rejected'
  | 'server_error'
  | 'unauthorized'
  | 'unknown'

type SaveError = {
  message?: string
  response?: {
    data?: { message?: string }
    status?: number
  }
}

export type CustomPinSettingPayload = {
  iglooWorkAvailable?: boolean
  iglooWorkCustomPinAvailable?: boolean
  keylessAccessAvailable?: boolean
}

export interface UseCustomPinSettingTrackingProps {
  editable: boolean
  persistedValue: boolean
  propertyId?: number
}

const CLIENT_AUTH_ERROR_MESSAGE = 'Not Authorized'

const ERROR_LEVEL_FAILURE_REASONS: CustomPinSettingFailureReason[] = [
  'network_error',
  'server_error',
]

export const getCustomPinSettingFailureReason = (
  status: number | undefined,
  message: string,
): CustomPinSettingFailureReason => {
  if (!status && message === CLIENT_AUTH_ERROR_MESSAGE) return 'unauthorized'
  if (!status) return 'network_error'
  if (status === 400 && message.includes('cannot be enabled while')) return 'parent_disabled'
  if (status === 400 && message.includes('not configured')) return 'integration_missing'
  if (status === 400) return 'rejected'
  if (status === 401) return 'unauthorized'
  if (status === 403) return 'forbidden'
  if (status === 409) return 'code_conflict'
  if (status >= 500) return 'server_error'
  return 'unknown'
}

export const useCustomPinSettingTracking = ({
  editable,
  persistedValue,
  propertyId,
}: UseCustomPinSettingTrackingProps) => {
  const { captureEvent } = useCaptureEvent()
  const role = useSelector(selectUserRole)
  const canOpenSetting = ABILITIES.Navbar.organisation.includes(role)
  const viewedPropertyIdRef = useRef<number | null>(null)

  const capture = (name: string, properties: Record<string, unknown>) =>
    captureEvent('user_action', {
      building_id: String(propertyId),
      integration_provider: INTEGRATION_PROVIDER,
      name,
      property_id: propertyId,
      screen: SCREEN,
      setting_name: SETTING_NAME,
      ...properties,
    })

  useEffect(() => {
    if (!propertyId) {
      viewedPropertyIdRef.current = null
      return
    }
    if (!canOpenSetting || viewedPropertyIdRef.current === propertyId) return
    viewedPropertyIdRef.current = propertyId
    capture('screen_view.property_settings', { editable, value: persistedValue })
  }, [canOpenSetting, editable, persistedValue, propertyId])

  const trackToggle = (newValue: boolean) => {
    if (!propertyId) return
    capture('setting.toggle', { new_value: newValue, previous_value: !newValue })
  }

  const trackSave = (
    request: Promise<CustomPinSettingPayload | undefined>,
    payload: CustomPinSettingPayload,
  ) => {
    const newValue = Boolean(payload.iglooWorkCustomPinAvailable)
    if (!propertyId || newValue === persistedValue) return

    const change = {
      attempt_id: generateId('attempt'),
      new_value: newValue,
      previous_value: persistedValue,
      source: payload.keylessAccessAvailable && payload.iglooWorkAvailable ? 'toggle' : 'cascade',
    }

    capture('setting.save_attempt', change)

    request.then(
      (building) => {
        capture('setting.save_result', { ...change, result: 'success' })
        const savedValue = Boolean(building?.iglooWorkCustomPinAvailable)
        if (savedValue !== persistedValue) {
          capture('setting.state_changed', { ...change, new_value: savedValue })
        }
      },
      (error: SaveError) => {
        const status = error?.response?.status
        const serverMessage = String(error?.response?.data?.message || error?.message || '')
        const failureReason = getCustomPinSettingFailureReason(status, serverMessage)
        const failure = {
          ...change,
          error_code: status,
          error_message: serverMessage.slice(0, MAX_ERROR_MESSAGE_LENGTH),
          failure_reason: failureReason,
        }
        capture('setting.save_result', { ...failure, result: 'failure' })
        Sentry.captureMessage(
          `${CUSTOM_PIN_SETTING_NOT_SAVED_MESSAGE} for property ${propertyId}`,
          {
            extra: failure,
            fingerprint: ['igloo-custom-pin-setting-not-saved', failureReason],
            level: ERROR_LEVEL_FAILURE_REASONS.includes(failureReason) ? 'error' : 'warning',
            tags: {
              failure_reason: failureReason,
              integration_provider: INTEGRATION_PROVIDER,
              property_id: propertyId,
              setting_name: SETTING_NAME,
            },
          },
        )
      },
    )
  }

  return { trackSave, trackToggle }
}
